import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMe, apiErrText,
  staffCompliancePackage, staffComplianceInstantiate, staffComplianceSetForm, staffComplianceAddItem,
  staffComplianceUpload, staffComplianceConfirm, staffComplianceReturn, staffComplianceWaive,
  staffComplianceUnwaive, staffComplianceDates, staffComplianceReuse, staffComplianceNudge,
  staffComplianceFileUrl, complianceChannelLine, fileSizeText,
  COMPLIANCE_STATUS, COMPLIANCE_CATEGORY, COMPLIANCE_CADENCE, COMPLIANCE_NOT_OPEN_REASON, CONTRACT_FORM_LABEL, COMPLIANCE_POLICY_ONLY_LABEL, complianceReturnLine,
  type MeResponse, type StaffComplianceView, type ComplianceItemView, type ContractForm, type ComplianceCadence, type EffectiveComplianceStatus,
} from '../lib/api';
import { Skeleton } from './ui/Skeleton';
import { toast } from './ui/Toast';

/**
 * 📁 履约档案 / Compliance file — the STAFF half (Layer 1 step 4B, Eric 9/18).
 *
 * What 改改 does here, in the order the card is laid out:
 *   1. 还缺什么 — the required documents 乙方 still owes, grouped by WHEN the contract asks for them.
 *   2. 有效期 — what is about to lapse (a confirmed 资质 with an expiry inside the warn window).
 *   3. the A–H grid — every slot, with 确认 / 退回 / 免除 / 代上传 / 复用 / 催一下 on the row.
 *
 * Two rules the layout exists to serve:
 *   · NO money, anywhere. 项目经理 (site_pm) gets 200 on the read and 403 on every write, so the action
 *     buttons are hidden for them rather than rendered-and-refused (decision D7).
 *   · `open: false` still renders. A package whose contract is not signed yet must say WHY the checklist
 *     is empty — silence there is exactly the 华控 9/16 failure mode (paper assumed, never recorded).
 */

const REVIEW_ROLES = ['gm', 'budget', 'procurement', 'commercial'];
const WAIVE_ROLES = ['gm', 'budget'];
/** Effective statuses the 催一下 button may chase — the server refuses anything else with nothing_to_chase. */
const CHASEABLE = ['missing', 'returned', 'expired', 'expiring'];
/** 还缺什么: a required item in one of these states is an outstanding obligation today. */
const MISSING_STATES = ['missing', 'returned', 'expired', 'lapsed'];
const CATEGORIES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
/** 还缺什么 groups by cadence in contract order — Object.keys would lose the type AND the order. */
const CADENCES: ComplianceCadence[] =
  ['once', 'pre_mobilization', 'monthly', 'per_event', 'at_completion', 'at_settlement', 'warranty'];

/** ISO instant or plain date → the YYYY-MM-DD a <input type="date"> wants. */
const dateInput = (v: string | null | undefined) => (v ? v.slice(0, 10) : '');
const statusOf = (s: EffectiveComplianceStatus) => COMPLIANCE_STATUS[s] ?? { label: s, pill: 'pill-muted' };

export default function CompliancePanel({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const { data, isLoading, isError, error } = useQuery<StaffComplianceView>({
    queryKey: ['compliance', 'package', pid],
    queryFn: () => staffCompliancePackage(pid),
    enabled: !!pid,
  });
  const [busy, setBusy] = useState(false);
  const role = me?.account?.role ?? '';
  const canReview = REVIEW_ROLES.includes(role);
  const canWaive = WAIVE_ROLES.includes(role);
  const reload = () => {
    qc.invalidateQueries({ queryKey: ['compliance', 'package', pid] });
    qc.invalidateQueries({ queryKey: ['compliance', 'queue'] });
  };

  if (isLoading) return <div className="card"><Skeleton lines={5} /></div>;
  if (isError) return <div className="card">读取履约档案失败 / could not load the compliance file: {apiErrText(error)}</div>;
  if (!data) return null;

  const { counts, items } = data;
  const outstanding = items.filter((i) => i.required && MISSING_STATES.includes(i.effective_status));
  const expiring = items.filter((i) => i.effective_status === 'expiring' || i.effective_status === 'expired');

  async function run(fn: () => Promise<unknown>, okMsg: string | ((r: unknown) => string)) {
    setBusy(true);
    try { const r = await fn(); toast(typeof okMsg === 'function' ? okMsg(r) : okMsg, { kind: 'success' }); reload(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <div>
      {/* ── header: who owes, under which contract form, and how complete the file is ───────────── */}
      <div className="card">
        <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
          <strong>📁 履约档案 / Compliance file</strong>
          {data.labor_co_name && <span className="pill pill-stage small">{data.labor_co_name}</span>}
          {data.contract_id && <span className="muted small">合同编号 / contract no. {data.contract_id}</span>}
          <span style={{ flex: 1 }} />
          {data.open && (
            <span className="small">
              完成度 / Completion <strong>{counts.required_done}/{counts.required_total}</strong>
              {counts.awaiting_review > 0 && <span className="pill pill-bad small" style={{ marginLeft: 6 }}>待审 {counts.awaiting_review}</span>}
              {counts.outstanding > 0 && <span className="pill pill-warn small" style={{ marginLeft: 6 }}>缺失 {counts.outstanding}</span>}
            </span>
          )}
        </div>

        {/* open:false is a STATE, not an error — the gate is SIGNED-only, so say WHICH reason it is.
            Silence here is exactly the 华控 9/16 failure mode: paper assumed, never recorded. */}
        {!data.open && (
          <div className="small" style={{ marginTop: 8, color: 'var(--muted)' }}>
            {COMPLIANCE_NOT_OPEN_REASON[data.reason ?? 'not_signed']}
            {data.reason === 'no_subcontract' && ' — 请先在「验收标准与合同」页生成分包合同。/ generate the contract on the Standards tab first.'}
            {data.subcontract_status && <span className="muted"> （当前状态 / status: {data.subcontract_status}）</span>}
          </div>
        )}

        <div className="row-wrap small" style={{ marginTop: 8, alignItems: 'center', gap: 12 }}>
          {/* 合同形式: null means inferContractForm could not read 固定单价/固定总价 off the template name
              (华控's real problem) — and then only form_applicability='both' items were instantiated. */}
          {data.contract_form
            ? <span className="muted">合同形式 / Contract form: <strong>{CONTRACT_FORM_LABEL[data.contract_form]}</strong></span>
            : canReview && data.subcontract_id
              ? (
                <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <span style={{ color: 'var(--bad)' }}>合同形式未知 / contract form unknown —</span>
                  <select disabled={busy} defaultValue=""
                    onChange={(e) => {
                      const v = e.target.value as ContractForm | '';
                      if (v) run(() => staffComplianceSetForm(data.subcontract_id!, v), '合同形式已设置 / contract form set');
                    }}>
                    <option value="">请选择 / pick one…</option>
                    <option value="unit_price">{CONTRACT_FORM_LABEL.unit_price}</option>
                    <option value="lump_sum">{CONTRACT_FORM_LABEL.lump_sum}</option>
                  </select>
                </span>
              )
              : <span className="muted">合同形式未知 / contract form unknown</span>}

          {data.executed_file && (
            <a className="btn small" href={staffComplianceFileUrl(data.executed_file.id)}>
              ⬇ 签章扫描件 / executed scan（{fileSizeText(data.executed_file.size_bytes)}）
            </a>
          )}
          {data.open && canWaive && data.subcontract_id && (
            <button className="small" disabled={busy}
              title="按当前目录和合同形式补齐缺少的清单项（已有项不受影响）/ top the checklist up from the catalog — existing slots are untouched"
              onClick={() => run(() => staffComplianceInstantiate(data.subcontract_id!), '清单已重建 / checklist rebuilt')}>
              重建清单 / Rebuild checklist
            </button>
          )}
          {data.open && canReview && data.subcontract_id && data.event_types.length > 0 && (
            <AddEventRecord scId={data.subcontract_id} types={data.event_types} busy={busy}
              onDone={() => reload()} />
          )}
        </div>
      </div>

      {!data.open && items.length === 0 ? null : (
        <>
          {/* ── 还缺什么 ────────────────────────────────────────────────────────────────────────── */}
          <div className="card" style={{ borderColor: outstanding.length ? 'var(--warn)' : undefined }}>
            <strong>还缺什么 / What's missing（{outstanding.length}）</strong>
            {outstanding.length === 0 && (
              <div className="muted small" style={{ marginTop: 4 }}>必备资料齐全 / every required document is on file.</div>
            )}
            {/* grouped by WHEN the contract asks for it (签约时 / 进场前 / 每月 …), not by category — that is
                the order 改改 chases them in. */}
            {outstanding.length > 0 && CADENCES
              .filter((cad) => outstanding.some((i) => i.cadence === cad))
              .map((cad) => (
                <div key={cad} className="small" style={{ marginTop: 6 }}>
                  <span className="muted">{COMPLIANCE_CADENCE[cad]}：</span>
                  {outstanding.filter((i) => i.cadence === cad).map((i) => (
                    <span key={i.id} style={{ marginRight: 10 }}>
                      {i.item_code} {i.label_zh}
                      {i.period_key ? `（${i.period_key}）` : ''}
                      <span className={`pill ${statusOf(i.effective_status).pill} small`} style={{ marginLeft: 4 }}>
                        {statusOf(i.effective_status).label}
                      </span>
                    </span>
                  ))}
                </div>
              ))}
          </div>

          {/* ── 有效期 ─────────────────────────────────────────────────────────────────────────── */}
          {expiring.length > 0 && (
            <div className="card" style={{ borderColor: 'var(--bad)' }}>
              <strong>有效期提醒 / Expiring &amp; expired（{expiring.length}）</strong>
              <div className="muted small">
                证照到期即视为缺失，付款前请先催更新。/ a lapsed certificate counts as missing — chase the renewal before the next payment.
              </div>
              {expiring.map((i) => (
                <div key={i.id} className="small" style={{ marginTop: 4 }}>
                  {i.item_code} {i.label_zh}
                  <span className={`pill ${statusOf(i.effective_status).pill} small`} style={{ marginLeft: 6 }}>{statusOf(i.effective_status).label}</span>
                  {i.expires_at && <span className="muted" style={{ marginLeft: 6 }}>有效期至 / valid through {dateInput(i.expires_at)}</span>}
                </div>
              ))}
            </div>
          )}

          {/* ── the A–H grid ──────────────────────────────────────────────────────────────────── */}
          {CATEGORIES.filter((c) => items.some((i) => i.category === c)).map((cat) => (
            <div className="card" key={cat}>
              <strong>{COMPLIANCE_CATEGORY[cat] ?? cat}</strong>
              {/* .cmp-wrap for the scroll shell, but NOT .cmp itself: its `white-space: nowrap` is right
                  for a price comparison and wrong here — seven columns of CJK would push 操作 off-screen. */}
              <div className="cmp-wrap">
                <table className="tbl-hover">
                  <thead><tr>
                    <th style={{ minWidth: 200 }}>项目 / Item</th>
                    <th>责任方 / Owner</th>
                    <th>频次 / Cadence</th>
                    <th>状态 / Status</th>
                    <th style={{ minWidth: 150 }}>文件 / Files</th>
                    <th>到期 / Dates</th>
                    <th style={{ minWidth: 170 }}>操作 / Actions</th>
                  </tr></thead>
                  <tbody>
                    {items.filter((i) => i.category === cat)
                      .sort((a, b) => a.sort_order - b.sort_order || a.item_code.localeCompare(b.item_code) || (a.period_key ?? '').localeCompare(b.period_key ?? ''))
                      .map((i) => (
                        <ItemRow key={i.id} item={i} view={data} canReview={canReview} canWaive={canWaive}
                          busy={busy} onRun={run} />
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** ＋新增记录 — opens another slot for a per_event type (材料报批, 隐蔽验收, 发票, 索赔…). */
function AddEventRecord(
  { scId, types, busy, onDone }:
  { scId: string; types: StaffComplianceView['event_types']; busy: boolean; onDone: () => void },
) {
  const [code, setCode] = useState('');
  const [working, setWorking] = useState(false);
  async function add() {
    if (!code) return;
    setWorking(true);
    try { await staffComplianceAddItem(scId, code); toast('已新增记录 / record added', { kind: 'success' }); setCode(''); onDone(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setWorking(false); }
  }
  return (
    <span className="row" style={{ gap: 6, alignItems: 'center' }}>
      <select value={code} disabled={busy || working} style={{ maxWidth: 240 }} onChange={(e) => setCode(e.target.value)}>
        <option value="">＋新增记录 / Add a record…</option>
        {types.map((t) => <option key={t.code} value={t.code}>{t.code} {t.label_zh} / {t.label_en}</option>)}
      </select>
      <button className="small" disabled={!code || busy || working} onClick={add}>新增 / Add</button>
    </span>
  );
}

/** One checklist slot. The action drawer opens under the row so the table stays readable. */
function ItemRow(
  { item, view, canReview, canWaive, busy, onRun }: {
    item: ComplianceItemView; view: StaffComplianceView;
    canReview: boolean; canWaive: boolean; busy: boolean;
    onRun: (fn: () => Promise<unknown>, okMsg: string | ((r: unknown) => string)) => Promise<void>;
  },
) {
  const [drawer, setDrawer] = useState<null | 'return' | 'waive' | 'upload' | 'reuse' | 'dates'>(null);
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<FileList | null>(null);
  const [expiresAt, setExpiresAt] = useState(dateInput(item.expires_at));
  const [note, setNote] = useState('');
  const [source, setSource] = useState('');
  const [dueAt, setDueAt] = useState(dateInput(item.due_at));
  const [hardAt, setHardAt] = useState(dateInput(item.hard_deadline_at));

  const st = statusOf(item.effective_status);
  const cooldownMin = Number(view.settings?.nudge_cooldown_min ?? 30);
  const nudgedRecently = !!item.last_nudged_at
    && (Date.now() - new Date(item.last_nudged_at).getTime()) / 60_000 < cooldownMin;
  const reusable = view.reusable.filter((r) => r.item_code === item.item_code);
  // review 9/18: the 代上传 and ✎改日期 drawers share expiresAt — an abandoned 代上传 date must not
  // pre-fill 改日期 and get saved as the real 有效期
  const close = () => {
    setDrawer(null); setReason(''); setFiles(null); setNote(''); setSource('');
    setExpiresAt(dateInput(item.expires_at)); setDueAt(dateInput(item.due_at)); setHardAt(dateInput(item.hard_deadline_at));
  };

  return (
    <>
      <tr>
        <td style={{ whiteSpace: 'normal' }}>
          <div><strong>{item.item_code}</strong> {item.label_zh}</div>
          <div className="muted small">{item.label_en}</div>
          <div className="row-wrap small" style={{ gap: 4, marginTop: 2 }}>
            {!item.required && <span className="pill pill-muted small">选填 / optional</span>}
            {/* 政策项: D4/G5 are DAILY contract duties collected as monthly roll-ups — a deliberate softening. */}
            {!!item.policy_only && <span className="pill pill-muted small">{COMPLIANCE_POLICY_ONLY_LABEL}</span>}
            {!!item.rights_extinguishing && <span className="pill pill-bad small" title="逾期未提交即丧失该项权利 / missing the bar extinguishes the right">失权条款 / rights-extinguishing</span>}
            {!!item.wtl_countersign && <span className="pill pill-stage small">需我方签认 / WTL countersign</span>}
            {item.scope === 'supplier' && <span className="pill pill-muted small">公司级 / supplier-level</span>}
          </div>
          {item.clause_ref && <div className="muted small">条款 / clause: {item.clause_ref}</div>}
          {item.evidence_hint_zh && <div className="muted small">需要提交：{item.evidence_hint_zh}</div>}
          {item.review_note_md && item.effective_status === 'returned' && (
            <div className="small" style={{ color: 'var(--bad)', marginTop: 2 }}>{complianceReturnLine(item.review_note_md)}</div>
          )}
          {item.waive_reason_md && item.effective_status === 'waived' && (
            <div className="muted small" style={{ marginTop: 2 }}>免除理由：{item.waive_reason_md}</div>
          )}
          {/* 江卫春 9/20: the contractor's 「无此资质」 lands here, where 免除 is one click away */}
          {item.waiver_requested_at && (item.status === 'missing' || item.status === 'returned') && (
            <div className="small" style={{ marginTop: 2, color: 'var(--accent)' }}>
              <span className="pill pill-warn small">劳务申请免除 / waiver requested</span>{' '}
              {item.waiver_requested_by ?? '劳务'} · {dateInput(item.waiver_requested_at)}：{item.waiver_request_md}
            </div>
          )}
          {!!item.on_behalf_of_labor && <div className="muted small">代劳务上传 / filed by WTL on their behalf</div>}
        </td>
        <td>{item.owner_side === 'labor' ? '乙方 / Labor co' : item.owner_side === 'wtl' ? '甲方（我方）/ WTL' : '双方 / Both'}</td>
        <td className="small">
          {COMPLIANCE_CADENCE[item.cadence] ?? item.cadence}
          {item.period_key && <div className="muted">{item.period_key}</div>}
        </td>
        <td><span className={`pill ${st.pill} small`}>{st.label}</span></td>
        <td className="small" style={{ whiteSpace: 'normal' }}>
          {item.files.length === 0 ? <span className="muted">—</span> : item.files.map((f) => (
            <div key={f.id}>
              <a href={staffComplianceFileUrl(f.file_id)} title={`${f.uploaded_side === 'labor' ? '劳务上传' : '我方上传'} · ${f.uploaded_by ?? ''}`}>
                ⬇ {f.file_name}
              </a>
              <span className="muted"> · {fileSizeText(f.size_bytes)}</span>
            </div>
          ))}
        </td>
        <td className="small">
          {item.expires_at && <div>有效期 / valid: {dateInput(item.expires_at)}</div>}
          {item.due_at && <div className="muted">要求 / due: {dateInput(item.due_at)}</div>}
          {item.hard_deadline_at && <div style={{ color: 'var(--bad)' }}>失权期限 / bar: {dateInput(item.hard_deadline_at)}</div>}
          {canReview && <button className="small" style={{ marginTop: 2 }} onClick={() => setDrawer(drawer === 'dates' ? null : 'dates')}>✎ 改日期 / Dates</button>}
        </td>
        <td className="small">
          {!canReview
            ? <span className="muted">只读 / read-only</span>
            : (
              <div className="row-wrap" style={{ gap: 4 }}>
                {item.status === 'uploaded' && (
                  <button className="btn-good small" disabled={busy} onClick={() => onRun(() => staffComplianceConfirm(item.id), '已确认 / confirmed')}>✅ 确认</button>
                )}
                {(item.status === 'uploaded' || item.status === 'confirmed') && (
                  <button className="small" disabled={busy} onClick={() => setDrawer(drawer === 'return' ? null : 'return')}>退回</button>
                )}
                {canWaive && item.status !== 'confirmed' && item.status !== 'waived' && (
                  <button className={`small${item.waiver_requested_at ? ' btn-warn' : ''}`} disabled={busy}
                    title={item.waiver_requested_at ? '劳务已申请免除 — 理由已预填 / the contractor asked for this; reason pre-filled' : undefined}
                    onClick={() => {
                      // pre-fill the contractor's reason so 免除 is a read-and-confirm, not a retype
                      if (drawer !== 'waive' && !reason && item.waiver_request_md) setReason(`劳务申请：${item.waiver_request_md}`);
                      setDrawer(drawer === 'waive' ? null : 'waive');
                    }}>免除{item.waiver_requested_at ? ' ⚑' : ''}</button>
                )}
                {canWaive && item.status === 'waived' && (
                  <button className="small" disabled={busy} onClick={() => onRun(() => staffComplianceUnwaive(item.id), '已恢复 / restored')}>恢复</button>
                )}
                <button className="small" disabled={busy} onClick={() => setDrawer(drawer === 'upload' ? null : 'upload')}>代上传</button>
                {reusable.length > 0 && (
                  <button className="small" disabled={busy} onClick={() => setDrawer(drawer === 'reuse' ? null : 'reuse')}>复用</button>
                )}
                {CHASEABLE.includes(item.effective_status) && (
                  <button className="small" disabled={busy || nudgedRecently}
                    title={nudgedRecently ? `${cooldownMin} 分钟内已催过 / nudged within the last ${cooldownMin} min` : '在工程线程里记录一条提醒 / record a reminder on the engagement thread'}
                    // review 9/18: through onRun so busy + reload apply — the cooldown greys the button in-session
                    onClick={() => { close(); void onRun(() => staffComplianceNudge(item.id), (r) => complianceChannelLine((r as { channels: Parameters<typeof complianceChannelLine>[0] }).channels)); }}>催一下</button>
                )}
              </div>
            )}
        </td>
      </tr>

      {drawer && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--stripe)', whiteSpace: 'normal' }}>
            {drawer === 'return' && (
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field grow" style={{ marginBottom: 0 }}>
                  <label>退回理由（必填，会原文发给劳务）/ reason (required — sent verbatim to the contractor)</label>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例如：营业执照照片不清晰，请重拍" />
                </div>
                <button className="btn-warn small" disabled={busy || !reason.trim()}
                  onClick={async () => { await onRun(() => staffComplianceReturn(item.id, reason.trim()), '已退回 / returned'); close(); }}>
                  确认退回 / Return
                </button>
                <button className="small" onClick={close}>取消 / Cancel</button>
              </div>
            )}

            {drawer === 'waive' && (
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field grow" style={{ marginBottom: 0 }}>
                  <label>免除理由（必填）/ waive reason (required)</label>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例如：本合同无保证人，该项不适用" />
                </div>
                <span className="muted small">免除 = 本项确实不适用，不是「还没催到」。/ waive means genuinely inapplicable, not "not chased yet".</span>
                <button className="btn-warn small" disabled={busy || !reason.trim()}
                  onClick={async () => { await onRun(() => staffComplianceWaive(item.id, reason.trim()), '已免除 / waived'); close(); }}>
                  确认免除 / Waive
                </button>
                <button className="small" onClick={close}>取消 / Cancel</button>
              </div>
            )}

            {drawer === 'upload' && (
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>文件（最多 10 个，每个 ≤30MB）/ files (≤10, 30 MB each)</label>
                  <input type="file" multiple accept=".pdf,.ofd,image/*,.xlsx,.xls,.docx,.doc"
                    onChange={(e) => setFiles(e.target.files)} />
                </div>
                {!!item.expiry_tracked && (
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>有效期（必填）/ valid through (required)</label>
                    <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                  </div>
                )}
                <div className="field grow" style={{ marginBottom: 0 }}>
                  <label>备注 / note</label>
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="微信收到的扫描件" />
                </div>
                <button className="btn-primary small" disabled={busy || !files || files.length === 0 || (!!item.expiry_tracked && !expiresAt)}
                  title={!!item.expiry_tracked && !expiresAt ? '请先填写有效期 / enter the validity date first' : ''}
                  onClick={async () => {
                    const fd = new FormData();
                    for (const f of Array.from(files ?? [])) fd.append('file', f);
                    if (expiresAt) fd.append('expires_at', expiresAt);
                    if (note.trim()) fd.append('note_md', note.trim());
                    fd.append('on_behalf', '1');   // 代上传: the slot records that WTL filed it, not 乙方
                    await onRun(() => staffComplianceUpload(item.id, fd), '已上传 / uploaded');
                    close();
                  }}>
                  代劳务上传 / Upload on their behalf
                </button>
                <button className="small" onClick={close}>取消 / Cancel</button>
              </div>
            )}

            {drawer === 'reuse' && (
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field grow" style={{ marginBottom: 0 }}>
                  <label>复用该公司其他项目已确认的资料 / copy a confirmed record forward</label>
                  <select value={source} onChange={(e) => setSource(e.target.value)}>
                    <option value="">请选择来源 / pick a source…</option>
                    {reusable.map((r) => (
                      <option key={r.item_id} value={r.item_id}>
                        {r.package_name ?? r.package_id}
                        {r.project_name ? `（${r.project_name}）` : ''}
                        {r.expires_at ? ` · 有效期至 ${dateInput(r.expires_at)}` : ''}
                        {` · ${r.files.length} 个文件`}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="muted small">复用后为「已上传待审」，需人工确认。/ a copy lands in review — never auto-confirmed.</span>
                <button className="btn-primary small" disabled={busy || !source}
                  onClick={async () => { await onRun(() => staffComplianceReuse(item.id, source), '已复用 / copied'); close(); }}>
                  复用 / Copy forward
                </button>
                <button className="small" onClick={close}>取消 / Cancel</button>
              </div>
            )}

            {drawer === 'dates' && (
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>有效期 / valid through</label>
                  <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>要求期限 / due</label>
                  <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>失权期限 / rights bar</label>
                  <input type="date" value={hardAt} onChange={(e) => setHardAt(e.target.value)} />
                </div>
                <button className="btn-primary small" disabled={busy}
                  onClick={async () => {
                    // an emptied input clears the column (server: explicit null); keys are always sent
                    await onRun(() => staffComplianceDates(item.id, {
                      expires_at: expiresAt || null, due_at: dueAt || null, hard_deadline_at: hardAt || null,
                    }), '日期已更新 / dates updated');
                    setDrawer(null);
                  }}>
                  保存日期 / Save
                </button>
                <button className="small" onClick={close}>取消 / Cancel</button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
